import { useMediaQuery, Box, AppBar, Toolbar, IconButton } from '@mui/material';
import { IconUI } from './IconUI';
import colors from './colors';
import { TextUI } from './Text';
import { iconName } from './iconNames';
import { onClickHandler } from './utilities/click_handler';
import { useNavigate } from 'react-router';
import React from 'react';
import { maxPageWidth } from './Page';

export type ResponsiveAppBarProps = {
  items: Array<{
    icon: iconName
    label: string
    path: string
  }>
  children: React.ReactNode
  showAppBar: boolean
  header?: React.ReactNode
};

export const ResponsiveAppBarUI: React.FC<ResponsiveAppBarProps & { showLogo?: boolean }> = ({ children, showAppBar, items, header }) => {
  const isLargeScreen = useMediaQuery('(min-width:600px)');
  const navigate = useNavigate();
  const leftBarWidth = '130px';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Top Bar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: (isLargeScreen && showAppBar) ? `calc(100% - ${leftBarWidth})` : '100%',
          height: '68px',
          backgroundColor: colors.white,
          borderBottomColor: colors.greige,
          borderBottomWidth: '1px',
          borderBottomStyle: 'solid',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100, // Ensure it's above other elements like AppBar
          marginLeft: (isLargeScreen && showAppBar) ? leftBarWidth : undefined,
        }}
      >
        {header
          ? <div style={{ maxWidth: maxPageWidth, width: '100%' }}>{header}</div>
          : (
              <img
                src="/assets/logo_black.svg"
                alt="Logo"
                width="40px"
              />
            )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: isLargeScreen ? 'row' : 'column',
          flexGrow: 1,
          height: `calc(100% - 68px - ${showAppBar && !isLargeScreen ? '63px' : '0px'})`,
          marginTop: '68px', // Offset for the fixed top bar
          marginBottom: showAppBar && !isLargeScreen ? '63px' : '0px', // Offset for the bottom AppBar on small screens
        }}
      >
        {/* AppBar */}
        {showAppBar && (
          <AppBar
            position={isLargeScreen ? 'relative' : 'fixed'}
            elevation={4}
            sx={{
              top: isLargeScreen ? '0' : 'auto',
              bottom: isLargeScreen ? 'auto' : '0',
              left: isLargeScreen ? '0' : 'auto',
              right: 'auto',
              width: isLargeScreen ? leftBarWidth : '100%',
              height: isLargeScreen ? '100%' : '63px',
              flexDirection: isLargeScreen ? 'column' : 'row',
              backgroundColor: colors.white,
            }}
          >
            <Toolbar
              sx={{
                flexDirection: isLargeScreen ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                paddingLeft: '40px',
                paddingRight: '40px',
              }}
            >
              {items.map(({ icon, label, path }, i) => (
                <IconButton sx={{ width: isLargeScreen ? '100%' : undefined }} onClick={onClickHandler(() => navigate(path))} key={i} color="inherit">
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: isLargeScreen ? 'row' : 'column',
                      alignItems: 'center',
                      gap: '5px',
                      width: isLargeScreen ? '100%' : undefined,
                      justifyContent: isLargeScreen ? 'start' : undefined,
                    }}
                  >
                    <IconUI name={icon} />
                    <TextUI>{label}</TextUI>
                  </div>
                </IconButton>
              ))}
            </Toolbar>
          </AppBar>
        )}

        {/* Main Content */}
        <Box
          sx={{
            flexGrow: 1,
            padding: isLargeScreen ? '0px 3em' : '0px 1em',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};
